import {useCallback, useEffect, useRef, useState} from 'react';
import {Field} from '../types';
import {
    DevicePositionError, forgetDevicePosition, gpsCoordinateOf, positionValues, readDevicePosition,
    UNANSWERED_AFTER_MS, unansweredPosition,
} from '../utils/deviceLocation';

interface DevicePositionState {
    /** A reading is under way. */
    locating: boolean;
    /** Why there is no position. */
    locationFailure: DevicePositionError | null;
    /** Try again: asks the device once more, or loads the page anew where only that can help. */
    readLocation: () => void;
}

/**
 * Fills the GPS fields of a form being created with the position of the
 * device. The form itself never shows those fields; a form without any is left
 * alone and the device is not asked.
 */
export const useDevicePosition = (
    fields: Field[],
    formId: string,
    onPosition: (values: Record<string, number>) => void,
): DevicePositionState => {
    const [locating, setLocating] = useState(false);
    const [failure, setFailure] = useState<DevicePositionError | null>(null);
    const latest = useRef({fields, onPosition});
    latest.current = {fields, onPosition};
    const mounted = useRef(true);
    const unanswered = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(unanswered.current);
        };
    }, []);

    const read = useCallback(() => {
        if (!latest.current.fields.some(field => gpsCoordinateOf(field))) {
            return;
        }
        setLocating(true);
        setFailure(null);
        // The form stops waiting, yet the reading goes on: an answer that comes later is still taken.
        clearTimeout(unanswered.current);
        unanswered.current = setTimeout(() => {
            if (mounted.current) {
                setLocating(false);
                setFailure(unansweredPosition());
            }
        }, UNANSWERED_AFTER_MS);
        readDevicePosition()
            .then((position) => {
                if (mounted.current) {
                    setFailure(null);
                    latest.current.onPosition(positionValues(latest.current.fields, position));
                }
            })
            .catch((reason: DevicePositionError) => {
                if (mounted.current) setFailure(reason);
            })
            .finally(() => {
                clearTimeout(unanswered.current);
                if (mounted.current) setLocating(false);
            });
    }, []);

    // A form opened from stored application data can get its GPS fields a moment later.
    const gpsFieldCodes = fields.filter(field => gpsCoordinateOf(field)).map(field => field.code).join(',');

    useEffect(() => {
        read();
    }, [formId, gpsFieldCodes, read]);

    const needsFreshPage = failure?.needsFreshPage === true;
    const readLocation = useCallback(() => {
        if (needsFreshPage) {
            window.location.reload();
            return;
        }
        forgetDevicePosition();
        read();
    }, [read, needsFreshPage]);

    // A reading asked for fields the form no longer has says nothing.
    const asked = gpsFieldCodes !== '';

    return {locating: asked && locating, locationFailure: asked ? failure : null, readLocation};
};
